import React, { useEffect, useRef, useState } from 'react';
import { isJwtExpired } from '../../utilities/token-utils';
import {
    CONNECTION_STATUS_CONNECTED,
    CONVERSATION_TYPE_EVENT,
    HTTPS,
    ILxsServiceAgentViewProps,
    INCOMING_ACTIVITY,
    InitWebChatParams,
    IStoredConversation,
    MAX_TRANSCRIPT_COUNT,
    SET_CONTEXT_EVENT,
    START_CONVERSATION_EVENT,
    STORAGE_KEY
} from './lxs-service-agent.data';

const LxsServiceAgentView = (props: ILxsServiceAgentViewProps) => {
    const {
        resources,
        CLASS_NAME,
        fetchDirectLineToken,
        writeStoredConversation,
        clearStoredConversation,
        getValidStoredConversation
    } = props;

    const { requestContext } = props.context.actionContext;
    const { token, isAuthenticated } = requestContext?.user;
    const { canonicalDomain } = props.context.request as { canonicalDomain: string };

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [userToken, setUserToken] = useState<string | null>(token);
    const [dlToken, setDlToken] = useState<string | null>(null);
    const [restartConversation, setRestartConversation] = useState<boolean>(false);

    const webchatRef = useRef<HTMLDivElement | null>(null);

    const initWebChat = ({
        directLineToken,
        savedConversation,
        userToken,
        userName,
        locale,
        container,
        writeStoredConversation
    }: InitWebChatParams): void => {
        const WebChat = window.WebChat;
        if (!WebChat) {
            return;
        }

        const directLine = WebChat.createDirectLine({
            token: directLineToken,
            conversationId: savedConversation?.conversationId
        });

        const isRestoring = Boolean(savedConversation);
        const transcript = savedConversation?.transcript.slice() || [];

        const persistBlob = (updatedFields: Partial<IStoredConversation> = {}) => {
            const base: IStoredConversation = {
                conversationId: savedConversation?.conversationId || '',
                directLineToken: String(directLineToken),
                userToken,
                transcript
            };
            const blob = { ...base, ...updatedFields };
            writeStoredConversation(blob);
        };

        const store = WebChat.createStore(
            {},
            () => (next: (arg0: any) => any) => (action: { type: string; payload: { activity: any } }) => {
                if (action.type === INCOMING_ACTIVITY && !isRestoring) {
                    transcript.push(action.payload.activity);
                    if (transcript.length > MAX_TRANSCRIPT_COUNT) {
                        transcript.splice(0, transcript.length - MAX_TRANSCRIPT_COUNT);
                    }
                    persistBlob();
                }
                return next(action);
            }
        );

        if (isRestoring && savedConversation) {
            savedConversation.transcript.forEach(activity => store.dispatch({ type: INCOMING_ACTIVITY, payload: { activity } }));
        }

        const connSub = directLine.connectionStatus$.subscribe((status: number) => {
            if (status === CONNECTION_STATUS_CONNECTED) {
                const convId = directLine.conversationId!;
                savedConversation = {
                    conversationId: convId,
                    directLineToken: String(directLineToken),
                    userToken,
                    transcript
                };
                persistBlob({ conversationId: convId });

                directLine
                    .postActivity({
                        from: { id: userName },
                        type: CONVERSATION_TYPE_EVENT,
                        name: SET_CONTEXT_EVENT,
                        value: { jwtLoopNeu: userToken, name: userName, BaseUrlLoop: HTTPS + canonicalDomain, locale }
                    })
                    .subscribe(
                        () => {
                            if (!isRestoring) {
                                directLine
                                    .postActivity({
                                        from: { id: userName },
                                        type: CONVERSATION_TYPE_EVENT,
                                        name: START_CONVERSATION_EVENT
                                    })
                                    .subscribe((err: any) => err && console.error(err));
                            }
                        },
                        (err: any) => console.error(err)
                    );

                connSub.unsubscribe();
            }
        });

        WebChat.renderWebChat(
            {
                directLine,
                userID: userName,
                locale,
                store,
                styleOptions: { hideUploadButton: true }
            },
            container
        );
    };

    // Effect: fetch Direct Line token (and refresh on restart)
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        let cancelled: boolean = false;

        const load = async () => {
            let savedConversation: IStoredConversation | null = null;

            try {
                const storedConversationRaw = sessionStorage.getItem(STORAGE_KEY);
                if (storedConversationRaw) {
                    savedConversation = JSON.parse(storedConversationRaw) as IStoredConversation;
                }
            } catch {
                clearStoredConversation();
            }

            if (savedConversation?.userToken === userToken) {
                setUserToken(token);
            }

            let directLineToken: string | null = null;

            // On restart, or first load, we may want to fetch a new token or reuse
            if (!dlToken || restartConversation) {
                if (!restartConversation && savedConversation?.userToken === userToken) {
                    directLineToken = savedConversation.directLineToken;
                } else {
                    // force a fresh token on restart
                    directLineToken = await fetchDirectLineToken(props.context);
                }
            } else {
                directLineToken = dlToken;
            }

            if (cancelled) {
                return;
            }

            if (directLineToken) {
                setDlToken(prev => (prev !== directLineToken ? directLineToken : prev));
            }
            setUserToken(token);
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [props.context.actionContext, restartConversation, fetchDirectLineToken, token, clearStoredConversation]);

    // Effect: init WebChat when dlToken changes (including after restart)
    useEffect(() => {
        if (typeof window === 'undefined' || !dlToken || !webchatRef.current) {
            return;
        }

        const directLineToken = dlToken;
        const userTokenForConversation = props.context.request.user.token;

        const savedConversation = getValidStoredConversation(directLineToken, userTokenForConversation);

        if (isJwtExpired(String(directLineToken))) {
            clearStoredConversation();
        }

        // on every dlToken change (including restart), re-init WebChat
        initWebChat({
            directLineToken,
            savedConversation,
            userToken: userTokenForConversation,
            userName: props.context.request.user.firstName,
            locale: props.context.request.locale,
            container: webchatRef.current,
            writeStoredConversation
        });
    }, [
        dlToken,
        props.context.request.user.token,
        props.context.request.user.firstName,
        props.context.request.locale,
        getValidStoredConversation,
        clearStoredConversation,
        writeStoredConversation
    ]);

    const handleRestart = () => {
        clearStoredConversation();
        setDlToken(null);
        setRestartConversation(prev => !prev);
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            <div className={CLASS_NAME}>
                <button
                    className={`${CLASS_NAME}__open-button${isOpen ? ` ${CLASS_NAME}__open-button--active` : ''}`}
                    onClick={() => setIsOpen(open => !open)}
                >
                    {resources.lxsServiceAgentName}
                </button>
            </div>
            <div
                className={`${CLASS_NAME}__frame${isOpen ? ` ${CLASS_NAME}__frame--open` : ` ${CLASS_NAME}__frame--closed`}${
                    isExpanded ? ` ${CLASS_NAME}__frame--expanded` : ''
                }`}
            >
                <div className={`${CLASS_NAME}__frame-heading`}>
                    <button
                        className={`${CLASS_NAME}__frame-${isExpanded ? 'minimize' : 'expand'}`}
                        title={resources.lxsServiceAgentExpandChatTitle}
                        onClick={() => setIsExpanded(expanded => !expanded)}
                    />
                    <h5 className={`${CLASS_NAME}__frame-heading-text`}>{resources.lxsServiceAgentTitle}</h5>
                    <button
                        className={`${CLASS_NAME}__reload`}
                        onClick={handleRestart}
                        title={resources.lxsServiceAgentRestartConversationTitle}
                    />
                </div>
                <div id={CLASS_NAME} ref={webchatRef} />
            </div>
        </>
    );
};

export default LxsServiceAgentView;
